import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cx } from './cx';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { Icon } from './Icon';
import { Select } from './Select';
import { typography } from './Text';
import { useT } from '../../modules/i18n';

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50];

export interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
  /** Current rows-per-page — required to render the size selector (paired
   * with `onPageSizeChange`). Omit both to keep this a plain page nav. */
  pageSize?: number;
  /** Supplying this renders a "rows per page" selector in the nav. When
   * present, the nav renders even if `totalPages <= 1` (so the user can still
   * change the page size on a single-page result); the prev/next/page-number
   * controls hide in that case since there's nothing to page through. */
  onPageSizeChange?: (pageSize: number) => void;
  /** Selectable page sizes, shown when `onPageSizeChange` is provided.
   * Defaults to the canonical `10 / 20 / 50`. */
  pageSizeOptions?: number[];
}

/** Compact page-number window around the current page (with first/last). */
function pageWindow(page: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | '…')[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(total - 1, page + 1);
  if (start > 2) out.push('…');
  for (let i = start; i <= end; i++) out.push(i);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

/**
 * The numbered-page row shared by both nav layouts below. Keyed by SLOT
 * POSITION (the item's index in the window array), not by page number: on
 * mid-range Next/Prev the window length is constant and every slot keeps its
 * meaning (e.g. slot 2 is always "page - 1"), so React updates each button's
 * label/active-state in place instead of unmounting/remounting and
 * reordering DOM nodes — that per-click remount + the buttons' own
 * `transition` was what caused the visible flicker. The two ellipsis slots
 * get their own stable, non-index keys (`left-ellipsis`/`right-ellipsis`)
 * since there are at most two and "which one" never changes meaning.
 */
function PageNumbers({
  page,
  totalPages,
  onPageChange,
  t,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  let ellipsisCount = 0;
  return (
    <div className="flex items-center gap-1">
      {pageWindow(page, totalPages).map((p, i) => {
        if (p === '…') {
          ellipsisCount += 1;
          return (
            <span
              key={ellipsisCount === 1 ? 'left-ellipsis' : 'right-ellipsis'}
              className="px-2 text-fg-muted"
            >
              …
            </span>
          );
        }
        return (
          <IconButton
            key={`slot-${i}`}
            aria-label={t('ui.pagination.goToPage', { page: p })}
            size="md"
            variant={p === page ? 'secondary' : 'ghost'}
            onPress={() => onPageChange(p)}
            className={cx(
              typography.button,
              p === page ? 'text-fg' : 'text-fg-tertiary',
            )}
          >
            {p}
          </IconButton>
        );
      })}
    </div>
  );
}

/**
 * Pagination footer — Previous / numbered pages / Next. Shared across the
 * catalog and any paginated table (replaces home/Pagination's local footer).
 */
export const Pagination = ({
  page,
  totalPages,
  onPageChange,
  className,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: PaginationProps) => {
  const t = useT();
  const showPageSize = Boolean(onPageSizeChange);

  // No new props supplied — byte-identical to the pre-page-size behavior,
  // including the early return on a single (or empty) page.
  if (!showPageSize) {
    if (totalPages <= 1) return null;
    return (
      <nav
        className={cx(
          'flex items-center justify-between gap-2 border-t border-default px-1 pt-3',
          className,
        )}
        aria-label={t('ui.pagination.ariaLabel')}
      >
        <Button
          variant="secondary"
          size="sm"
          isDisabled={page <= 1}
          onPress={() => onPageChange(page - 1)}
          iconLeading={<Icon as={ChevronLeft} size="sm" />}
        >
          {t('ui.pagination.previous')}
        </Button>
        <PageNumbers
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          t={t}
        />
        <Button
          variant="secondary"
          size="sm"
          isDisabled={page >= totalPages}
          onPress={() => onPageChange(page + 1)}
          iconTrailing={<Icon as={ChevronRight} size="sm" />}
        >
          {t('ui.pagination.next')}
        </Button>
      </nav>
    );
  }

  // A page-size selector was requested — the nav renders even on a single
  // page (so the size can still be changed), but the prev/next/page-number
  // controls hide since there's nothing to page through.
  const hasPages = totalPages > 1;
  const sizeOptions = pageSizeOptions.map(size => ({
    value: String(size),
    label: t('ui.pagination.pageSizeOption', { size }),
  }));

  return (
    <nav
      className={cx(
        'flex items-center gap-2 border-t border-default px-1 pt-3',
        hasPages ? 'justify-between' : 'justify-end',
        className,
      )}
      aria-label={t('ui.pagination.ariaLabel')}
    >
      {hasPages && (
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            isDisabled={page <= 1}
            onPress={() => onPageChange(page - 1)}
            iconLeading={<Icon as={ChevronLeft} size="sm" />}
          >
            {t('ui.pagination.previous')}
          </Button>
          <PageNumbers
            page={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
            t={t}
          />
          <Button
            variant="secondary"
            size="sm"
            isDisabled={page >= totalPages}
            onPress={() => onPageChange(page + 1)}
            iconTrailing={<Icon as={ChevronRight} size="sm" />}
          >
            {t('ui.pagination.next')}
          </Button>
        </div>
      )}
      <Select
        variant="pill"
        label={t('ui.pagination.pageSizeLabel')}
        options={sizeOptions}
        value={String(pageSize ?? pageSizeOptions[0])}
        onChange={v => onPageSizeChange?.(Number(v))}
      />
    </nav>
  );
};
